/**
 * The page's one bar.
 *
 * Three things changed when the site grew from one document to five, and each
 * was a defect in its own right:
 *
 *   - **The nav pointed at a repository.** "Belgeler · Kararlar · Depo" sent a
 *     visitor to GitHub, which is not where a reader of a commercial product
 *     page goes, and not somewhere this product's source belongs. It now points
 *     at the four pages this site actually has.
 *   - **The language control was a bare word.** A link reading "English" among
 *     five other links is indistinguishable from a sixth destination — a reader
 *     cannot tell it changes the language of the page they are on rather than
 *     taking them to an English page *about* something. It is now a labelled
 *     two-state control that shows both languages and marks the current one.
 *   - **There was no way to leave the dark theme.** The palette has carried a
 *     verified light theme all along, and this page hard-coded the dark one into
 *     its bytes.
 *
 * The language control stays an anchor, because it still changes the document:
 * `/tr/guvenlik/` and `/en/security/` are two prerendered files, not two states
 * of one. It resolves the *other* locale's slug for the page the reader is on —
 * a naive `/tr/` ↔ `/en/` swap drops everybody on a subpage back to the home
 * page — and picks up the fragment on the client, for the reason given where
 * that is done.
 */
import { useEffect, useRef, useState } from 'react'

import { useLocale, useT } from '../i18n/context'
import { Logo } from '../components/Logo'
import { CONSOLE_URL } from '../data/links'
import { NAV_PAGES, pathFor, type PageId } from '../routes'
import { ThemeToggle } from '../components/ThemeToggle'
import type { MessageKey } from '../i18n'

/** The nav label for each page. A record rather than a template string, so a
 *  new page cannot compile until somebody has written its label in both
 *  catalogues. */
const NAV_LABEL: Record<PageId, MessageKey> = {
  home: 'nav.home',
  product: 'nav.product',
  security: 'nav.security',
  company: 'nav.company',
  contact: 'nav.contact',
}

export function SiteHeader({ page }: { page: PageId }) {
  const t = useT()
  const locale = useLocale()
  const other = locale === 'tr' ? 'en' : 'tr'

  /*
   * The menu, on the widths where the page links do not fit.
   *
   * A five-page site whose header simply drops its navigation below 860px is a
   * site with no navigation on a telephone — which is most of the traffic a
   * Turkish B2B page gets from a forwarded link. So the links are never removed
   * from the document; the same `<nav>` is a row on a wide screen and a panel on
   * a narrow one, and only a data attribute and CSS decide which.
   *
   * Escape closes it because that is what Escape does, and because the panel
   * covers the page it opened over. There is no click-outside handler: every
   * item in it is a link to another document, so the panel cannot outlive the
   * decision to use it.
   */
  const [menuOpen, setMenuOpen] = useState(false)
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  /*
   * Carry the fragment across the language switch.
   *
   * Done to the element rather than to the rendered `href`, because the
   * prerendered bytes must not contain one: the hash is a fact about where this
   * reader has scrolled to, and baking `#security` into a static file would send
   * every visitor who ever opens the English page to the middle of it. So the
   * document ships the plain path, works without JavaScript, and is upgraded
   * here — which is also why it is an anchor with a real href and not a click
   * handler: a handler breaks middle-click and "open in new tab", and those are
   * how a reader compares two languages side by side.
   */
  const switchRef = useRef<HTMLAnchorElement>(null)
  const target = pathFor(other, page)
  useEffect(() => {
    const el = switchRef.current
    if (!el) return
    const apply = () => {
      el.setAttribute('href', target + window.location.hash)
    }
    apply()
    window.addEventListener('hashchange', apply)
    return () => window.removeEventListener('hashchange', apply)
  }, [target])

  return (
    <>
      {/*
        First in the tab order, and visible only once focused. A page of figures
        behind six controls is exactly the page a keyboard reader should be able
        to step past in one keystroke.
      */}
      <a className="k-skip-link" href="#main">
        {t('nav.skip')}
      </a>
      <header className="kl-header">
        <a className="kl-header__brand" href={pathFor(locale, 'home')}>
          <Logo size={26} />
          <span className="kl-header__word">Kimene</span>
        </a>

        <nav
          id="site-nav"
          className="kl-header__nav"
          aria-label={t('nav.site')}
          data-open={menuOpen ? 'true' : 'false'}
        >
          {NAV_PAGES.map((id) => (
            <a
              key={id}
              className={`kl-header__link kl-header__link--secondary ${
                id === page ? 'is-current' : ''
              }`}
              href={pathFor(locale, id)}
              /* The page a reader is on is announced, not merely underlined.
                 `aria-current` is the only part of "you are here" that reaches
                 somebody who cannot see the rule under the word. */
              aria-current={id === page ? 'page' : undefined}
            >
              {t(NAV_LABEL[id])}
            </a>
          ))}

          {/* The console door lives in the nav rather than beside the controls,
              and that is a layout fix as much as a semantic one: at 390px the
              English "Open the console" pushed the control row 10px past the
              viewport and gave every English page a horizontal scrollbar. In the
              nav it is a row item on a wide screen and an item in the panel on a
              narrow one — one copy, reachable in both. */}
          <a className="kl-header__link kl-header__link--console" href={CONSOLE_URL}>
            {t('nav.console')}
          </a>
        </nav>

        <div className="kl-header__tools">
          {/* Only ever visible on the narrow layout, where CSS gives it back its
              display. A button at `display: none` is out of the accessibility
              tree as well as off the screen, so the wide layout is not offering
              a control that does nothing. */}
          <button
            type="button"
            className="kl-menu__button"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            aria-label={t(menuOpen ? 'nav.menuClose' : 'nav.menuOpen')}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
              stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              {menuOpen ? (
                <>
                  <line x1="5" y1="5" x2="19" y2="19" />
                  <line x1="19" y1="5" x2="5" y2="19" />
                </>
              ) : (
                <>
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="17" x2="20" y2="17" />
                </>
              )}
            </svg>
          </button>

          <ThemeToggle />

          {/*
            Both languages are shown and the current one is marked, rather than
            offering only the one you would switch to. A reader who does not
            read the current language cannot tell what "English" means next to a
            page of Turkish — whether it is where they are or where they would
            go. Two items with one marked answers that without a word of copy.
          */}
          <div className="kl-locale" role="group" aria-label={t('nav.locale')}>
            <span className="kl-locale__current" aria-current="true">
              {locale.toUpperCase()}
            </span>
            <a
              ref={switchRef}
              className="kl-locale__other"
              href={target}
              hrefLang={other}
              lang={other}
              data-locale-switch=""
              aria-label={t('nav.localeSwitch', { language: t('nav.localeOther') })}
            >
              {other.toUpperCase()}
            </a>
          </div>
        </div>
      </header>
    </>
  )
}
